import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { statsApi, postsApi, newsletterApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  FileText,
  Eye,
  Heart,
  FolderOpen,
  Tags,
  Layers,
  BookOpen,
  Plus,
  ArrowRight,
  Clock,
} from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsData, postsData] = await Promise.all([
        statsApi.getStats(),
        postsApi.getAllPosts({ limit: 5 }),
      ]);
      setStats(statsData);
      setRecentPosts(postsData.posts);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-muted rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    { label: 'Yayınlanan', value: stats?.posts?.published || 0, icon: FileText, color: 'text-accent' },
    { label: 'Taslak', value: stats?.posts?.drafts || 0, icon: Clock, color: 'text-muted-foreground' },
    { label: 'Görüntülenme', value: stats?.views || 0, icon: Eye, color: 'text-accent' },
    { label: 'Tepki', value: stats?.reactions || 0, icon: Heart, color: 'text-accent' },
  ];

  const contentStats = [
    { label: 'Kategori', value: stats?.categories || 0, icon: FolderOpen, href: '/admin/categories' },
    { label: 'Etiket', value: stats?.tags || 0, icon: Tags, href: '/admin/tags' },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground">TheAnalystRoom yönetim paneline hoş geldiniz</p>
        </div>
        <Link to="/admin/posts/new">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4 mr-2" />
            Yeni Yazı
          </Button>
        </Link>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.label} className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color} opacity-80`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Content Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {contentStats.map((stat) => (
          <Link key={stat.label} to={stat.href}>
            <Card className="bg-card border-border hover:border-accent/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted rounded-sm flex items-center justify-center">
                    <stat.icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-foreground">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Posts */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="font-serif text-lg">Son Yazılar</CardTitle>
          <Link to="/admin/posts">
            <Button variant="ghost" size="sm" className="text-muted-foreground">
              Tümünü Gör
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentPosts.length > 0 ? (
            <div className="space-y-3">
              {recentPosts.map((post) => (
                <Link
                  key={post._id}
                  to={`/admin/posts/${post._id}`}
                  className="flex items-center justify-between p-3 -mx-3 rounded-sm hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{post.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${
                        post.status === 'published' 
                          ? 'bg-accent/10 text-accent' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {post.status === 'published' ? 'Yayında' : 'Taslak'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(post.created_at).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Henüz yazı bulunmuyor
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
